import React, { useState, useEffect, useCallback } from 'react';
import { Activity, RefreshCw, Loader, ChevronDown, ChevronUp, Move, Eye, CheckSquare, X as XIcon, Columns, Square } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useWarehouse } from '../../../../contexts/WarehouseContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';
import { stcBinService } from '../../../../services/stcBinService';
import { locationHeatmapService } from '../../../../services/locationHeatmapService';
import { heatmapMoveService } from '../../../../services/heatmapMoveService';
import { RackSummary, RackStructure } from '../rack-builder/types';
import { LocationHeatmapDto, HeatmapFilterState } from './types';
import {
    PlanMoveResponse,
    PlanBatchMoveResponse,
    ExecuteBatchMoveResponse,
    BatchMoveSource,
    SuggestDestinationsResponse,
    ActiveMoveBin,
    HeatmapMoveReason,
    HeatmapMovePriority
} from './heatmapMoveTypes';
import { HeatmapFilterPanel } from './HeatmapFilterPanel';
import { HeatmapRackGrid } from './HeatmapRackGrid';
import { InteractiveHeatmapRackGrid } from './InteractiveHeatmapRackGrid';
import { HeatmapBatchMoveModal } from './HeatmapBatchMoveModal';
import { BinContextMenu } from './BinContextMenu';
import { SuggestAndMoveModal } from './SuggestAndMoveModal';
import { DualRackView } from './DualRackView';
import { AddToPlanModal, PlanStepToAdd } from './AddToPlanModal';

/**
 * Location Heatmap Application
 *
 * Displays warehouse rack locations with heat colors based on activity levels
 * Optimized for large screens to display maximum locations
 */

interface LocationHeatmapAppProps {
  onBack?: () => void;
}

export const LocationHeatmapApp: React.FC<LocationHeatmapAppProps> = ({ onBack }) => {
    const { user } = useAuth();
    const { currentWarehouse } = useWarehouse();
    const { access: moduleAccess } = useModuleAccess();
    const { t } = useTranslation();
    const [racks, setRacks] = useState<RackSummary[]>([]);
    const [selectedRackNumber, setSelectedRackNumber] = useState<string | null>(null);
    const [rackStructure, setRackStructure] = useState<RackStructure | null>(null);
    const [heatmapData, setHeatmapData] = useState<Map<string, LocationHeatmapDto>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingHeatmap, setIsLoadingHeatmap] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isRackPanelExpanded, setIsRackPanelExpanded] = useState(true);
    const [toastMessage, setToastMessage] = useState<string | null>(null);

    // Edit-mode state (drag&drop) — single move
    const [editMode, setEditMode] = useState(false);
    const [moveSource, setMoveSource] = useState<string | null>(null);
    const [moveTarget, setMoveTarget] = useState<string | null>(null);
    const [planResponse, setPlanResponse] = useState<PlanMoveResponse | null>(null);
    const [isLoadingPlan, setIsLoadingPlan] = useState(false);
    const [planError, setPlanError] = useState<string | null>(null);
    const [isExecuting, setIsExecuting] = useState(false);
    const [executeError, setExecuteError] = useState<string | null>(null);

    // Multi-select + batch-move state
    const [selectedBinCodes, setSelectedBinCodes] = useState<Set<string>>(new Set());
    const [batchSources, setBatchSources] = useState<string[] | null>(null);
    const [batchTarget, setBatchTarget] = useState<string | null>(null);
    const [batchPlan, setBatchPlan] = useState<PlanBatchMoveResponse | null>(null);
    const [isLoadingBatchPlan, setIsLoadingBatchPlan] = useState(false);
    const [batchPlanError, setBatchPlanError] = useState<string | null>(null);
    const [isExecutingBatch, setIsExecutingBatch] = useState(false);
    const [batchExecuteError, setBatchExecuteError] = useState<string | null>(null);
    const [batchExecuteResult, setBatchExecuteResult] = useState<ExecuteBatchMoveResponse | null>(null);

    // Dual-rack view state (Fase 2C)
    const [viewMode, setViewMode] = useState<'single' | 'dual'>('single');
    const [dualRightRackNumber, setDualRightRackNumber] = useState<string | null>(null);
    const [dualRightRackStructure, setDualRightRackStructure] = useState<RackStructure | null>(null);
    const [dualRightHeatmapData, setDualRightHeatmapData] = useState<Map<string, LocationHeatmapDto>>(new Map());
    const [isLoadingDualRight, setIsLoadingDualRight] = useState(false);

    // Empty bins (candidate destinations) — loaded per rack in edit-mode
    const [emptyBinCodes, setEmptyBinCodes] = useState<Set<string>>(new Set());
    const [dualRightEmptyBinCodes, setDualRightEmptyBinCodes] = useState<Set<string>>(new Set());

    // Active-move bins — pending/in-progress moves affecting these bins. Map<binCode, activities[]>
    const [activeMoveBins, setActiveMoveBins] = useState<Map<string, ActiveMoveBin[]>>(new Map());
    const [dualRightActiveMoveBins, setDualRightActiveMoveBins] = useState<Map<string, ActiveMoveBin[]>>(new Map());

    // Add-to-plan state (heatmap → flytteplan-integrasjon)
    const [addToPlanSteps, setAddToPlanSteps] = useState<PlanStepToAdd[] | null>(null);
    const [addToPlanContext, setAddToPlanContext] = useState<string>('');

    // Context-menu + Suggest/Empty-bin state
    const [contextMenu, setContextMenu] = useState<{ binCode: string; x: number; y: number } | null>(null);
    const [suggestMode, setSuggestMode] = useState<'suggest' | 'empty-bin' | null>(null);
    const [suggestBin, setSuggestBin] = useState<string | null>(null);
    const [suggestData, setSuggestData] = useState<SuggestDestinationsResponse | null>(null);
    const [isLoadingSuggest, setIsLoadingSuggest] = useState(false);
    const [suggestLoadError, setSuggestLoadError] = useState<string | null>(null);
    const [isExecutingSuggest, setIsExecutingSuggest] = useState(false);
    const [suggestExecuteError, setSuggestExecuteError] = useState<string | null>(null);
    const [suggestExecuteResult, setSuggestExecuteResult] = useState<ExecuteBatchMoveResponse | null>(null);

    const companyNo = user?.companyNo || '';
    const warehouseID = currentWarehouse || '1';
    const canEdit = moduleAccess.heatmapMoveActive;

    // Default filter state - last 30 days
    const getDefaultFilterState = (): HeatmapFilterState => {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);

        return {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0],
            transType: null,
            includeWeekends: false,
            includeHolidays: false
        };
    };

    const [filterState, setFilterState] = useState<HeatmapFilterState>(getDefaultFilterState());

    // Load racks on mount
    useEffect(() => {
        loadRacks();
    }, [companyNo, warehouseID]);

    // Deep-link fra Reolbygger: ?module=location-heatmap&rack=A(&bin=A1307).
    // Reolen velges og lastes; bin highlightes og scrolles til i visningsgriden.
    const [deepLinkBin, setDeepLinkBin] = useState<string | null>(null);
    useEffect(() => {
        if (!companyNo) return;
        const params = new URLSearchParams(window.location.search);
        const rackParam = params.get('rack');
        const binParam = params.get('bin');
        if (rackParam) {
            setSelectedRackNumber(rackParam);
            loadRackStructure(rackParam);
            if (binParam) setDeepLinkBin(binParam);
        }
        // Kun ved oppstart — loadRackStructure er stabil nok for engangskjøring
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyNo]);

    /**
     * Load all racks for warehouse
     */
    const loadRacks = async () => {
        if (!companyNo) return;
        
        setIsLoading(true);
        setError(null);
        
        try {
            const data = await stcBinService.getRackSummary(companyNo, warehouseID);
            setRacks(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load racks');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Load empty bin codes for a rack prefix (edit-mode only).
     * Updates the given setter with a Set of bin codes.
     */
    const loadEmptyBins = async (
        rackNumber: string,
        setter: (s: Set<string>) => void
    ) => {
        if (!companyNo || !rackNumber) {
            setter(new Set());
            return;
        }
        try {
            const prefix = rackNumber.substring(0, 2);
            const bins = await heatmapMoveService.getEmptyBins(warehouseID, prefix);
            setter(new Set(bins));
        } catch {
            // Empty-bins is a progressive enhancement — don't surface the error loudly.
            setter(new Set());
        }
    };

    /**
     * Load active-move bins (bins with pending reservations/orders/plan steps).
     * Groups by binCode into a Map so cells can look up all activities at once.
     */
    const loadActiveMoveBins = async (
        rackNumber: string,
        setter: (m: Map<string, ActiveMoveBin[]>) => void
    ) => {
        if (!companyNo || !rackNumber) {
            setter(new Map());
            return;
        }
        try {
            const prefix = rackNumber.substring(0, 2);
            const rows = await heatmapMoveService.getActiveMoveBins(warehouseID, prefix);
            const grouped = new Map<string, ActiveMoveBin[]>();
            for (const row of rows) {
                const existing = grouped.get(row.binCode);
                if (existing) existing.push(row);
                else grouped.set(row.binCode, [row]);
            }
            setter(grouped);
        } catch {
            // Progressive enhancement — silent fail.
            setter(new Map());
        }
    };

    /**
     * Re-fetch all edit-mode overlays for a rack — called after a successful move
     * so the visual indicators (heatmap colours, empty, active-move) reflect reality.
     * In dual-view it ALSO refreshes the right rack so e.g. a plan step from rack A
     * to rack B lights up immediately on both panels.
     */
    const refreshEditOverlays = useCallback(async (rackNumber: string | null) => {
        // Left rack (or the single rack in single-view)
        const leftRack = rackNumber || selectedRackNumber;
        if (leftRack) {
            await loadHeatmapData(leftRack);
            if (editMode) {
                loadEmptyBins(leftRack, setEmptyBinCodes);
                loadActiveMoveBins(leftRack, setActiveMoveBins);
            }
        }
        // Right rack (only in dual-view). Refresh heatmap + overlays inline —
        // can't call loadDualRightRack here because it's declared later (TDZ).
        if (viewMode === 'dual' && dualRightRackNumber && companyNo) {
            const rightPrefix = dualRightRackNumber.substring(0, 2);
            try {
                const rightData = await locationHeatmapService.getHeatmapAnalysis({
                    startDate: filterState.startDate + 'T00:00:00',
                    endDate: filterState.endDate + 'T23:59:59',
                    locationPrefix: rightPrefix,
                    transType: filterState.transType,
                    includeWeekends: filterState.includeWeekends,
                    includeHolidays: filterState.includeHolidays
                });
                const rightMap = new Map<string, LocationHeatmapDto>();
                rightData.forEach(item => rightMap.set(item.binCode, item));
                setDualRightHeatmapData(rightMap);
            } catch {
                // Silent — heatmap-data refresh failure should not abort the move flow.
            }
            loadEmptyBins(dualRightRackNumber, setDualRightEmptyBinCodes);
            loadActiveMoveBins(dualRightRackNumber, setDualRightActiveMoveBins);
        }
    }, [editMode, viewMode, dualRightRackNumber, selectedRackNumber, companyNo, filterState]);

    /**
     * Load rack structure when rack is selected
     */
    const loadRackStructure = async (rackNumber: string) => {
        if (!companyNo) return;

        setIsLoading(true);
        setError(null);
        
        try {
            const structure = await stcBinService.getRackStructure(companyNo, warehouseID, rackNumber);
            setRackStructure(structure);
            
            // Automatically load heatmap data for the selected rack
            await loadHeatmapData(rackNumber);
            // Always fetch edit-mode overlays (empty + active-move bins). Component
            // state is cheap, and they're only rendered when in edit-mode anyway.
            // Avoids a stale-closure bug where handleDualLeftChange (useCallback with
            // [] deps) captured loadRackStructure when editMode was still false.
            loadEmptyBins(rackNumber, setEmptyBinCodes);
            loadActiveMoveBins(rackNumber, setActiveMoveBins);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load rack structure');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Load heatmap data
     */
    const loadHeatmapData = async (rackNumber?: string) => {
        const targetRack = rackNumber || selectedRackNumber;
        if (!targetRack) return;

        setIsLoadingHeatmap(true);
        setError(null);
        
        try {
            // Get location prefix from rack number (first 2 characters)
            const locationPrefix = targetRack.substring(0, 2);

            const request = {
                startDate: filterState.startDate + 'T00:00:00',
                endDate: filterState.endDate + 'T23:59:59',
                locationPrefix: locationPrefix,
                transType: filterState.transType,
                includeWeekends: filterState.includeWeekends,
                includeHolidays: filterState.includeHolidays
            };

            const data = await locationHeatmapService.getHeatmapAnalysis(request);
            
            // Convert array to Map for quick lookup
            const dataMap = new Map<string, LocationHeatmapDto>();
            data.forEach(item => {
                dataMap.set(item.binCode, item);
            });
            
            setHeatmapData(dataMap);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load heatmap data');
        } finally {
            setIsLoadingHeatmap(false);
        }
    };

    /**
     * Handle rack selection
     */
    const handleRackSelect = (rackNumber: string) => {
        setSelectedRackNumber(rackNumber);
        loadRackStructure(rackNumber);
    };

    /**
     * Reset filters to default
     */
    const handleResetFilters = () => {
        setFilterState(getDefaultFilterState());
    };

    /**
     * Apply filters - reload heatmap data
     */
    const handleApplyFilters = () => {
        if (selectedRackNumber) {
            loadHeatmapData();
        }
    };

    /**
     * Called when a user drops a bin onto another bin in edit mode.
     * Routes through the batch flow so ALL items in the source bin are
     * pre-selected (user can deselect or adjust quantities). This is the
     * natural interpretation of "drag bin A onto bin B" — move everything.
     */
    const handleBinMoveRequested = useCallback(async (fromBinCode: string, toBinCode: string) => {
        // Inline the batch-open logic to avoid referencing handleBatchMoveRequested
        // before declaration. Behaviour: "drag A onto B" = pre-select all items in A.
        setBatchSources([fromBinCode]);
        setBatchTarget(toBinCode);
        setBatchPlan(null);
        setBatchPlanError(null);
        setBatchExecuteError(null);
        setBatchExecuteResult(null);
        setIsLoadingBatchPlan(true);

        try {
            const plan = await heatmapMoveService.planBatch({
                warehouseID,
                toBinCode,
                fromBinCodes: [fromBinCode]
            });
            setBatchPlan(plan);
        } catch (err) {
            setBatchPlanError(err instanceof Error ? err.message : t('manager.modules.locationHeatmap.errors.planningFailed', {}, 'Ukjent feil ved planlegging'));
        } finally {
            setIsLoadingBatchPlan(false);
        }
    }, [warehouseID, t]);

    const handleCloseModal = useCallback(() => {
        setMoveSource(null);
        setMoveTarget(null);
        setPlanResponse(null);
        setPlanError(null);
        setExecuteError(null);
    }, []);

    const handleConfirmMove = useCallback(async (params: {
        itemNo: string;
        quantity: number;
        reason: HeatmapMoveReason;
        priority: HeatmapMovePriority;
        comment: string;
    }) => {
        if (!moveSource || !moveTarget) return;
        setIsExecuting(true);
        setExecuteError(null);

        try {
            const result = await heatmapMoveService.execute({
                warehouseID,
                fromBinCode: moveSource,
                toBinCode: moveTarget,
                itemNo: params.itemNo,
                quantity: params.quantity,
                reason: params.reason,
                priority: params.priority,
                comment: params.comment || undefined
            });

            if (!result.success) {
                setExecuteError(result.errorMessage || t('manager.modules.locationHeatmap.errors.moveFailed', {}, 'Flytt kunne ikke fullføres'));
                return;
            }

            setToastMessage(
                t('manager.modules.locationHeatmap.toast.singleMoveCreated', { '0': result.stcOrderNo, '1': params.quantity, '2': params.itemNo, '3': moveSource, '4': moveTarget }, `Flyttordre #${result.stcOrderNo} opprettet. ${params.quantity} stk ${params.itemNo} er reservert for flytt fra ${moveSource} til ${moveTarget}.`)
            );
            window.setTimeout(() => setToastMessage(null), 6000);

            handleCloseModal();

            // Refresh heatmap + edit overlays so the new activity shows up.
            refreshEditOverlays(selectedRackNumber);
        } catch (err) {
            setExecuteError(err instanceof Error ? err.message : t('manager.modules.locationHeatmap.errors.moveError', {}, 'Ukjent feil ved flytt'));
        } finally {
            setIsExecuting(false);
        }
    }, [moveSource, moveTarget, warehouseID, selectedRackNumber, handleCloseModal, t]);

    const handleToggleEditMode = useCallback(() => {
        if (!canEdit) return;
        setEditMode(prev => {
            const turningOn = !prev;
            if (turningOn && selectedRackNumber) {
                // Eager-load empty bins + active-move bins when entering edit-mode.
                loadEmptyBins(selectedRackNumber, setEmptyBinCodes);
                loadActiveMoveBins(selectedRackNumber, setActiveMoveBins);
            } else if (!turningOn) {
                setEmptyBinCodes(new Set());
                setDualRightEmptyBinCodes(new Set());
                setActiveMoveBins(new Map());
                setDualRightActiveMoveBins(new Map());
            }
            return turningOn;
        });
        handleCloseModal();
        setSelectedBinCodes(new Set());
        setViewMode('single');
    }, [canEdit, handleCloseModal, selectedRackNumber]);

    const handleToggleViewMode = useCallback(() => {
        setViewMode(prev => prev === 'single' ? 'dual' : 'single');
        setSelectedBinCodes(new Set());
    }, []);

    /** Load structure + heatmap for a specific rack into the right-pane state. */
    const loadDualRightRack = useCallback(async (rackNumber: string) => {
        if (!companyNo || !rackNumber) return;
        setIsLoadingDualRight(true);
        try {
            const structure = await stcBinService.getRackStructure(companyNo, warehouseID, rackNumber);
            setDualRightRackStructure(structure);

            const locationPrefix = rackNumber.substring(0, 2);
            const data = await locationHeatmapService.getHeatmapAnalysis({
                startDate: filterState.startDate + 'T00:00:00',
                endDate: filterState.endDate + 'T23:59:59',
                locationPrefix,
                transType: filterState.transType,
                includeWeekends: filterState.includeWeekends,
                includeHolidays: filterState.includeHolidays
            });
            const map = new Map<string, LocationHeatmapDto>();
            data.forEach(item => map.set(item.binCode, item));
            setDualRightHeatmapData(map);

            // Load empty bins + active-move bins for the right rack too.
            loadEmptyBins(rackNumber, setDualRightEmptyBinCodes);
            loadActiveMoveBins(rackNumber, setDualRightActiveMoveBins);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load right rack');
        } finally {
            setIsLoadingDualRight(false);
        }
    }, [companyNo, warehouseID, filterState]);

    const handleDualLeftChange = useCallback((rackNumber: string) => {
        setSelectedRackNumber(rackNumber);
        loadRackStructure(rackNumber);
    }, []);

    const handleDualRightChange = useCallback((rackNumber: string) => {
        setDualRightRackNumber(rackNumber);
        if (rackNumber) loadDualRightRack(rackNumber);
    }, [loadDualRightRack]);

    const handleDualSwap = useCallback(() => {
        const oldLeft = selectedRackNumber;
        const oldRight = dualRightRackNumber;
        if (!oldLeft || !oldRight) return;
        setSelectedRackNumber(oldRight);
        setDualRightRackNumber(oldLeft);
        loadRackStructure(oldRight);
        loadDualRightRack(oldLeft);
    }, [selectedRackNumber, dualRightRackNumber, loadDualRightRack]);

    /** Toggle selection for a bin. Modes: 'single' (replace), 'add' (Ctrl+klikk), 'range' (Shift+klikk — treated as add here). */
    const handleToggleSelect = useCallback((binCode: string, mode: 'single' | 'add' | 'range') => {
        setSelectedBinCodes(prev => {
            const next = new Set(prev);
            if (mode === 'single') {
                // Replace selection with just this bin, unless clicking an already-single selected = deselect.
                if (prev.size === 1 && prev.has(binCode)) {
                    next.clear();
                } else {
                    next.clear();
                    next.add(binCode);
                }
            } else {
                // 'add' and 'range' both toggle without clearing
                if (next.has(binCode)) {
                    next.delete(binCode);
                } else {
                    next.add(binCode);
                }
            }
            return next;
        });
    }, []);

    const handleClearSelection = useCallback(() => {
        setSelectedBinCodes(new Set());
    }, []);

    /** Triggered when user drops a multi-selection on a destination bin. */
    const handleBatchMoveRequested = useCallback(async (sources: string[], toBinCode: string) => {
        setBatchSources(sources);
        setBatchTarget(toBinCode);
        setBatchPlan(null);
        setBatchPlanError(null);
        setBatchExecuteError(null);
        setBatchExecuteResult(null);
        setIsLoadingBatchPlan(true);

        try {
            const plan = await heatmapMoveService.planBatch({
                warehouseID,
                toBinCode,
                fromBinCodes: sources
            });
            setBatchPlan(plan);
        } catch (err) {
            setBatchPlanError(err instanceof Error ? err.message : t('manager.modules.locationHeatmap.errors.planningFailed', {}, 'Ukjent feil ved planlegging'));
        } finally {
            setIsLoadingBatchPlan(false);
        }
    }, [warehouseID, t]);

    const handleCloseBatchModal = useCallback(() => {
        setBatchSources(null);
        setBatchTarget(null);
        setBatchPlan(null);
        setBatchPlanError(null);
        setBatchExecuteError(null);
        setBatchExecuteResult(null);
    }, []);

    const handleConfirmBatchMove = useCallback(async (params: {
        sources: BatchMoveSource[];
        reason: HeatmapMoveReason;
        priority: HeatmapMovePriority;
        comment: string;
    }) => {
        if (!batchTarget) return;
        setIsExecutingBatch(true);
        setBatchExecuteError(null);

        try {
            const result = await heatmapMoveService.executeBatch({
                warehouseID,
                toBinCode: batchTarget,
                sources: params.sources,
                reason: params.reason,
                priority: params.priority,
                comment: params.comment || undefined
            });

            setBatchExecuteResult(result);

            if (result.success && result.failedCount === 0) {
                setToastMessage(
                    t('manager.modules.locationHeatmap.toast.batchMoveCreated', { '0': result.stcOrderNo, '1': result.successCount }, `Flyttordre #${result.stcOrderNo} opprettet med ${result.successCount} linjer.`)
                );
                window.setTimeout(() => setToastMessage(null), 6000);
                setSelectedBinCodes(new Set());
                refreshEditOverlays(selectedRackNumber);
            } else if (result.success) {
                // Partial success — keep modal open so user can see failed lines
                setToastMessage(
                    t('manager.modules.locationHeatmap.toast.batchMovePartial', { '0': result.stcOrderNo, '1': result.successCount, '2': result.failedCount }, `Flyttordre #${result.stcOrderNo}: ${result.successCount} lykkes, ${result.failedCount} feilet.`)
                );
                window.setTimeout(() => setToastMessage(null), 8000);
                refreshEditOverlays(selectedRackNumber);
            } else {
                setBatchExecuteError(result.errorMessage || t('manager.modules.locationHeatmap.errors.noLinesExecuted', {}, 'Ingen linjer kunne utføres'));
            }
        } catch (err) {
            setBatchExecuteError(err instanceof Error ? err.message : t('manager.modules.locationHeatmap.errors.moveError', {}, 'Ukjent feil ved flytt'));
        } finally {
            setIsExecutingBatch(false);
        }
    }, [batchTarget, warehouseID, selectedRackNumber, t]);

    /** Toolbar action: trigger batch move from current selection without dragging. Uses modal picker approach. */
    // Note: when using the toolbar "Flytt samlet"-button, the user still needs to drop on a destination.
    // For MVP the drag&drop is the only way to pick destination — toolbar just shows the count + clear.

    // ===== Add-to-plan handlers =====

    /** Called from HeatmapMoveModal "Legg i plan" — single move. */
    const handleAddSingleToPlan = useCallback((params: {
        itemNo: string;
        quantity: number;
        batchNo?: string;
        serialNo?: string;
    }) => {
        if (!moveSource || !moveTarget) return;
        setAddToPlanSteps([{
            fromBinCode: moveSource,
            toBinCode: moveTarget,
            itemNo: params.itemNo,
            quantity: params.quantity,
            batchNo: params.batchNo,
            serialNo: params.serialNo
        }]);
        setAddToPlanContext(`${moveSource} → ${moveTarget}`);
        handleCloseModal();
    }, [moveSource, moveTarget, handleCloseModal]);
    // Context label "A → B" is a bin-code→bin-code identifier, not translatable prose.

    /** Called from HeatmapBatchMoveModal "Legg i plan" — many sources, one destination. */
    const handleAddBatchToPlan = useCallback((sources: BatchMoveSource[]) => {
        if (!batchTarget) return;
        const planSteps: PlanStepToAdd[] = sources.map(s => ({
            fromBinCode: s.fromBinCode,
            toBinCode: s.toBinCodeOverride || batchTarget,
            itemNo: s.itemNo,
            quantity: s.quantity
        }));
        setAddToPlanSteps(planSteps);
        setAddToPlanContext(t('manager.modules.locationHeatmap.planContext.batch', { '0': sources.length, '1': batchTarget }, `${sources.length} kilder → ${batchTarget}`));
        handleCloseBatchModal();
    }, [batchTarget, handleCloseBatchModal, t]);

    /** Called from SuggestAndMoveModal "Legg i plan" — per-line destinations from PutAwayService. */
    const handleAddSuggestToPlan = useCallback((sources: BatchMoveSource[]) => {
        if (!suggestBin) return;
        const planSteps: PlanStepToAdd[] = sources.map(s => ({
            fromBinCode: s.fromBinCode,
            toBinCode: s.toBinCodeOverride || '',  // Always set from suggestion
            itemNo: s.itemNo,
            quantity: s.quantity
        }));
        setAddToPlanSteps(planSteps);
        setAddToPlanContext(t('manager.modules.locationHeatmap.planContext.suggest', { '0': suggestBin, '1': sources.length }, `${suggestBin} (${sources.length} varer) → ulike destinasjoner`));
        // Inline cleanup of suggest-modal state (can't call closeSuggestModal —
        // it's declared later in the file).
        setSuggestMode(null);
        setSuggestBin(null);
        setSuggestData(null);
        setSuggestLoadError(null);
        setSuggestExecuteError(null);
        setSuggestExecuteResult(null);
    }, [suggestBin, t]);

    const handlePlanAdded = useCallback((planId: number, planName: string, addedCount: number) => {
        setAddToPlanSteps(null);
        setAddToPlanContext('');
        setToastMessage(
            t('manager.modules.locationHeatmap.toast.planAdded', { '0': addedCount, '1': planName }, `${addedCount} steg lagt til i plan "${planName}". Åpne Flytteplaner for å reservere destinasjoner og utføre.`)
        );
        window.setTimeout(() => setToastMessage(null), 8000);
        setSelectedBinCodes(new Set());
        // Re-fetch active-move bins so the newly added plan steps get orange rings.
        refreshEditOverlays(selectedRackNumber);
    }, [refreshEditOverlays, selectedRackNumber, t]);

    const handleCloseAddToPlan = useCallback(() => {
        setAddToPlanSteps(null);
        setAddToPlanContext('');
    }, []);

    // ===== Context menu + Suggest/Empty-bin handlers =====

    const handleBinContextMenu = useCallback((binCode: string, x: number, y: number) => {
        setContextMenu({ binCode, x, y });
    }, []);

    const closeContextMenu = useCallback(() => setContextMenu(null), []);

    const openSuggestFlow = useCallback(async (binCode: string, mode: 'suggest' | 'empty-bin') => {
        setContextMenu(null);
        setSuggestMode(mode);
        setSuggestBin(binCode);
        setSuggestData(null);
        setSuggestLoadError(null);
        setSuggestExecuteError(null);
        setSuggestExecuteResult(null);
        setIsLoadingSuggest(true);

        try {
            const result = await heatmapMoveService.suggest({
                warehouseID,
                fromBinCode: binCode
            });
            setSuggestData(result);
            if (!result.success && result.errorMessage) {
                setSuggestLoadError(result.errorMessage);
            }
        } catch (err) {
            setSuggestLoadError(err instanceof Error ? err.message : t('manager.modules.locationHeatmap.errors.suggestFailed', {}, 'Ukjent feil ved forslag'));
        } finally {
            setIsLoadingSuggest(false);
        }
    }, [warehouseID, t]);

    const handleContextMenuAction = useCallback((actionId: 'suggest' | 'empty-bin') => {
        if (!contextMenu) return;
        openSuggestFlow(contextMenu.binCode, actionId);
    }, [contextMenu, openSuggestFlow]);

    const closeSuggestModal = useCallback(() => {
        setSuggestMode(null);
        setSuggestBin(null);
        setSuggestData(null);
        setSuggestLoadError(null);
        setSuggestExecuteError(null);
        setSuggestExecuteResult(null);
    }, []);

    const handleConfirmSuggestMove = useCallback(async (params: {
        sources: BatchMoveSource[];
        reason: HeatmapMoveReason;
        priority: HeatmapMovePriority;
        comment: string;
    }) => {
        setIsExecutingSuggest(true);
        setSuggestExecuteError(null);

        try {
            // Each source has its own toBinCodeOverride (from the suggestions).
            const result = await heatmapMoveService.executeBatch({
                warehouseID,
                toBinCode: undefined, // Per-line destinations
                sources: params.sources,
                reason: params.reason,
                priority: params.priority,
                comment: params.comment || undefined
            });

            setSuggestExecuteResult(result);

            if (result.success && result.failedCount === 0) {
                setToastMessage(
                    t('manager.modules.locationHeatmap.toast.suggestMoveCreated', { '0': result.stcOrderNo, '1': result.successCount }, `Flyttordre #${result.stcOrderNo} opprettet med ${result.successCount} linjer fra regelmotor-forslag.`)
                );
                window.setTimeout(() => setToastMessage(null), 6000);
                refreshEditOverlays(selectedRackNumber);
            } else if (result.success) {
                setToastMessage(
                    t('manager.modules.locationHeatmap.toast.batchMovePartial', { '0': result.stcOrderNo, '1': result.successCount, '2': result.failedCount }, `Flyttordre #${result.stcOrderNo}: ${result.successCount} lykkes, ${result.failedCount} feilet.`)
                );
                window.setTimeout(() => setToastMessage(null), 8000);
                refreshEditOverlays(selectedRackNumber);
            } else {
                setSuggestExecuteError(result.errorMessage || t('manager.modules.locationHeatmap.errors.noLinesExecuted', {}, 'Ingen linjer kunne utføres'));
            }
        } catch (err) {
            setSuggestExecuteError(err instanceof Error ? err.message : t('manager.modules.locationHeatmap.errors.unknown', {}, 'Ukjent feil'));
        } finally {
            setIsExecutingSuggest(false);
        }
    }, [warehouseID, selectedRackNumber, refreshEditOverlays, t]);

    return (
        <div className="flex flex-col h-screen w-full overflow-hidden bg-gray-50">
            {/* Toolbar - Compact */}
            <div className="flex items-center justify-between bg-white px-4 py-2 border-b border-gray-200 shadow-sm flex-shrink-0">
                <div className="flex items-center space-x-3">
                    <BackButton onClick={onBack || (() => {})} />
                    <Activity className="w-5 h-5 text-blue-600" />
                    <div>
                        <h1 className="text-lg font-bold text-gray-800">{t('manager.modules.locationHeatmap.title', {}, 'Lokasjon HeatMap')}</h1>
                        <p className="text-xs text-gray-600">
                            {t('manager.modules.locationHeatmap.header.warehouseCompany', { '0': warehouseID, '1': companyNo }, `Lager: ${warehouseID} | Selskap: ${companyNo}`)}
                        </p>
                    </div>
                </div>
                
                <div className="flex items-center space-x-2">
                    {canEdit && editMode && (
                        <button
                            onClick={handleToggleViewMode}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-sm font-semibold border ${
                                viewMode === 'dual'
                                    ? 'bg-purple-100 border-purple-300 text-purple-700'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                            title={viewMode === 'dual' ? t('manager.modules.locationHeatmap.toolbar.singleRackViewTooltip', {}, 'Gå til enkel reol-visning') : t('manager.modules.locationHeatmap.toolbar.dualRackViewTooltip', {}, 'Vis to reoler side-om-side')}
                        >
                            {viewMode === 'dual' ? <Square className="w-4 h-4" /> : <Columns className="w-4 h-4" />}
                            <span>{viewMode === 'dual' ? t('manager.modules.locationHeatmap.toolbar.singleRack', {}, 'Én reol') : t('manager.modules.locationHeatmap.toolbar.dualRack', {}, 'To reoler')}</span>
                        </button>
                    )}
                    {canEdit && (
                        <button
                            onClick={handleToggleEditMode}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors text-sm font-semibold ${
                                editMode
                                    ? 'bg-amber-500 text-white hover:bg-amber-600'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                            title={editMode ? t('manager.modules.locationHeatmap.toolbar.exitEditTooltip', {}, 'Gå tilbake til visning') : t('manager.modules.locationHeatmap.toolbar.enterEditTooltip', {}, 'Aktiver drag&drop-flytt')}
                        >
                            {editMode ? <Eye className="w-4 h-4" /> : <Move className="w-4 h-4" />}
                            <span>{editMode ? t('manager.modules.locationHeatmap.toolbar.exitEdit', {}, 'Avslutt redigering') : t('manager.modules.locationHeatmap.toolbar.enterEdit', {}, 'Rediger lager')}</span>
                        </button>
                    )}
                    {selectedRackNumber && (
                        <button
                            onClick={() => window.open(`/?module=rack-builder&rack=${encodeURIComponent(selectedRackNumber)}`, '_blank')}
                            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                            title={t('manager.modules.locationHeatmap.toolbar.openInRackBuilderTooltip', {}, 'Åpne denne reolen i Reolbygger (nytt vindu)')}
                        >
                            <span>{t('manager.modules.locationHeatmap.toolbar.openInRackBuilder', {}, 'Åpne i Reolbygger')}</span>
                        </button>
                    )}
                    <button
                        onClick={loadRacks}
                        disabled={isLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                        <span>{t('common.buttons.refresh', {}, 'Oppdater')}</span>
                    </button>
                </div>
            </div>

            {/* Toast message after successful move */}
            {toastMessage && (
                <div className="bg-green-50 border-b border-green-200 text-green-800 px-4 py-2 text-sm flex-shrink-0 flex items-center justify-between">
                    <span>{toastMessage}</span>
                    <button
                        onClick={() => setToastMessage(null)}
                        className="text-green-700 hover:text-green-900 font-semibold text-xs"
                    >
                        {t('common.buttons.close', {}, 'Lukk')}
                    </button>
                </div>
            )}

            {/* Multi-select floating toolbar — shown in edit-mode with ≥1 selection */}
            {editMode && selectedBinCodes.size > 0 && (
                <div className="bg-purple-50 border-b border-purple-200 px-4 py-2 flex-shrink-0 flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-2 text-purple-900">
                        <CheckSquare className="w-4 h-4" />
                        <span className="font-semibold">
                            {selectedBinCodes.size === 1
                                ? t('manager.modules.locationHeatmap.selection.countSingular', { '0': selectedBinCodes.size }, `${selectedBinCodes.size} lokasjon valgt`)
                                : t('manager.modules.locationHeatmap.selection.countPlural', { '0': selectedBinCodes.size }, `${selectedBinCodes.size} lokasjoner valgt`)}
                        </span>
                        <span className="text-xs text-purple-700 ml-2">
                            {selectedBinCodes.size === 1
                                ? t('manager.modules.locationHeatmap.selection.hintSingle', {}, '(høyreklikk eller bruk "Foreslå" for plan-modus)')
                                : t('manager.modules.locationHeatmap.selection.hintMulti', {}, '(dra én av dem for å flytte alle til samme destinasjon)')}
                        </span>
                    </div>
                    <div className="flex items-center space-x-1">
                        {selectedBinCodes.size === 1 && (
                            <button
                                onClick={() => {
                                    const binCode = Array.from(selectedBinCodes)[0];
                                    openSuggestFlow(binCode, 'suggest');
                                }}
                                className="flex items-center space-x-1 px-2 py-1 text-xs bg-amber-100 hover:bg-amber-200 text-amber-900 rounded font-semibold"
                            >
                                <span>{t('manager.modules.locationHeatmap.actions.suggestPlacement', {}, 'Foreslå plassering')}</span>
                            </button>
                        )}
                        <button
                            onClick={handleClearSelection}
                            className="flex items-center space-x-1 px-2 py-1 text-xs text-purple-700 hover:bg-purple-100 rounded"
                        >
                            <XIcon className="w-3 h-3" />
                            <span>{t('manager.modules.locationHeatmap.selection.clear', {}, 'Fjern valg')}</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Error message */}
            {error && (
                <div className="bg-red-50 border-b border-red-200 text-red-800 px-4 py-2 text-sm flex-shrink-0">
                    {error}
                </div>
            )}

            {/* Rack selector - COLLAPSIBLE RIBBON STYLE (hidden in dual-view) */}
            {viewMode === 'single' && (
            <div className="bg-white border-b border-gray-200 flex-shrink-0">
                {/* Ribbon header */}
                <div 
                    className="flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setIsRackPanelExpanded(!isRackPanelExpanded)}
                >
                    <div className="flex items-center space-x-3">
                        <h2 className="text-sm font-semibold text-gray-800">
                            {t('manager.modules.locationHeatmap.rackSelector.title', {}, 'Velg Reol')}
                        </h2>
                        {selectedRackNumber && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                                {t('manager.modules.locationHeatmap.rackSelector.rackBadge', { '0': selectedRackNumber }, `Reol ${selectedRackNumber}`)}
                            </span>
                        )}
                    </div>
                    
                    <div className="flex items-center space-x-2">
                        {selectedRackNumber && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedRackNumber(null);
                                    setRackStructure(null);
                                    setHeatmapData(new Map());
                                }}
                                className="text-xs text-gray-600 hover:text-gray-800 underline"
                            >
                                {t('manager.modules.locationHeatmap.selection.clear', {}, 'Fjern valg')}
                            </button>
                        )}
                        {isRackPanelExpanded ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                    </div>
                </div>

                {/* Rack grid - collapsible */}
                {isRackPanelExpanded && (
                    <div className="px-4 pb-3 border-t border-gray-100 animate-in slide-in-from-top duration-200">
                        {isLoading && racks.length === 0 ? (
                            <div className="flex items-center justify-center py-4">
                                <Loader className="w-5 h-5 animate-spin text-blue-600" />
                                <span className="ml-2 text-sm text-gray-600">{t('manager.modules.locationHeatmap.rackSelector.loadingRacks', {}, 'Laster reoler...')}</span>
                            </div>
                        ) : racks.length === 0 ? (
                            <div className="text-center py-4 text-sm text-gray-500">
                                {t('manager.modules.locationHeatmap.rackSelector.noRacks', {}, 'Ingen reoler funnet.')}
                            </div>
                        ) : (
                            <div className="grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12 xl:grid-cols-16 gap-2 mt-3">
                                {racks.map(rack => (
                                    <button
                                        key={rack.rackNumber}
                                        onClick={() => handleRackSelect(rack.rackNumber)}
                                        className={`p-2 rounded border-2 transition-all text-left ${
                                            selectedRackNumber === rack.rackNumber
                                                ? 'bg-blue-100 border-blue-500 shadow-sm'
                                                : 'bg-white border-gray-200 hover:border-blue-300 hover:shadow'
                                        }`}
                                    >
                                        <div className="text-sm font-bold text-gray-800">
                                            {t('manager.modules.locationHeatmap.rackSelector.rackBadge', { '0': rack.rackNumber }, `Reol ${rack.rackNumber}`)}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                            {t('manager.modules.locationHeatmap.rackSelector.locationCountShort', { '0': rack.locationCount }, `${rack.locationCount} lok`)}
                                        </div>
                                        <div className="text-[10px] text-gray-500">
                                            V{rack.minVertical}-{rack.maxVertical} × H{rack.minShelf}-{rack.maxShelf}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
            )}

            {/* Dual-rack view (edit-mode + viewMode==dual) */}
            {editMode && viewMode === 'dual' && (
                <div className="flex-1 min-h-0 flex p-2">
                    <DualRackView
                        allRacks={racks}
                        leftRackNumber={selectedRackNumber}
                        rightRackNumber={dualRightRackNumber}
                        leftRackStructure={rackStructure}
                        rightRackStructure={dualRightRackStructure}
                        leftHeatmapData={heatmapData}
                        rightHeatmapData={dualRightHeatmapData}
                        isLoadingLeft={isLoading || isLoadingHeatmap}
                        isLoadingRight={isLoadingDualRight}
                        onLeftRackChange={handleDualLeftChange}
                        onRightRackChange={handleDualRightChange}
                        onSwap={handleDualSwap}
                        onBinMoveRequested={handleBinMoveRequested}
                        onBatchMoveRequested={handleBatchMoveRequested}
                        selectedBinCodes={selectedBinCodes}
                        onToggleSelect={handleToggleSelect}
                        onBinContextMenu={handleBinContextMenu}
                        leftEmptyBinCodes={emptyBinCodes}
                        rightEmptyBinCodes={dualRightEmptyBinCodes}
                        leftActiveMoveBins={activeMoveBins}
                        rightActiveMoveBins={dualRightActiveMoveBins}
                    />
                </div>
            )}

            {/* Main content area - 2 columns (single-rack view) */}
            {viewMode === 'single' && selectedRackNumber && rackStructure && (
                <div 
                    className="flex-1 min-h-0 flex gap-2 p-2" 
                    style={{ maxHeight: isRackPanelExpanded ? 'calc(100vh - 240px)' : 'calc(100vh - 160px)' }}
                >
                    {/* Filter panel (Left) */}
                    <div className="w-64 bg-white rounded-lg shadow flex flex-col flex-shrink-0">
                        <div className="bg-white border-b border-gray-200 px-3 py-2 flex-shrink-0">
                            <h3 className="font-semibold text-sm text-gray-800">{t('manager.modules.locationHeatmap.filter.panelTitle', {}, 'Filtrer periode')}</h3>
                        </div>
                        <div className="flex-1 overflow-y-auto p-3">
                            <HeatmapFilterPanel
                                filterState={filterState}
                                onFilterChange={setFilterState}
                                onReset={handleResetFilters}
                                onApply={handleApplyFilters}
                                isLoading={isLoadingHeatmap}
                            />
                        </div>
                    </div>

                    {/* Heatmap grid (Center) */}
                    <div className="flex-1 bg-white rounded-lg shadow flex flex-col min-w-0">
                        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 flex-shrink-0">
                            <h3 className="font-semibold text-sm text-gray-800">
                                {t('manager.modules.locationHeatmap.grid.activityMapTitle', { '0': selectedRackNumber }, `Aktivitetskart - Reol ${selectedRackNumber}`)}
                            </h3>
                            {heatmapData.size > 0 && (
                                <span className="text-xs text-gray-600">
                                    {t('manager.modules.locationHeatmap.grid.locationsWithActivity', { '0': heatmapData.size }, `${heatmapData.size} lokasjoner med aktivitet`)}
                                </span>
                            )}
                        </div>
                        
                        <div className="flex-1 overflow-hidden p-2">
                            {isLoading || isLoadingHeatmap ? (
                                <div className="flex items-center justify-center h-full">
                                    <Loader className="w-6 h-6 animate-spin text-blue-600" />
                                    <span className="ml-2 text-sm text-gray-600">
                                        {isLoading ? t('manager.modules.locationHeatmap.grid.loadingRack', {}, 'Laster reol...') : t('manager.modules.locationHeatmap.grid.loadingHeatmap', {}, 'Laster heatmap data...')}
                                    </span>
                                </div>
                            ) : editMode ? (
                                <InteractiveHeatmapRackGrid
                                    rackStructure={rackStructure}
                                    heatmapData={heatmapData}
                                    onBinMoveRequested={handleBinMoveRequested}
                                    onBatchMoveRequested={handleBatchMoveRequested}
                                    selectedBinCodes={selectedBinCodes}
                                    onToggleSelect={handleToggleSelect}
                                    onBinContextMenu={handleBinContextMenu}
                                    emptyBinCodes={emptyBinCodes}
                                    activeMoveBins={activeMoveBins}
                                />
                            ) : (
                                <HeatmapRackGrid
                                    rackStructure={rackStructure}
                                    heatmapData={heatmapData}
                                    highlightBinCode={deepLinkBin}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Single-source drags now also route through the batch modal below, so
                users always see the full list of items in the source bin and can
                select all or specific lines. The old HeatmapMoveModal single-item
                dropdown is no longer rendered — the state it used is retained only
                for potential future use (e.g. a precision single-item shortcut). */}

            {/* Batch-move modal (single OR multi-select drop) */}
            {editMode && batchSources && batchTarget && (
                <HeatmapBatchMoveModal
                    isOpen={true}
                    toBinCode={batchTarget}
                    sourceBinCodes={batchSources}
                    isLoadingPlan={isLoadingBatchPlan}
                    plan={batchPlan}
                    planError={batchPlanError}
                    isExecuting={isExecutingBatch}
                    executeError={batchExecuteError}
                    executeResult={batchExecuteResult}
                    onClose={handleCloseBatchModal}
                    onConfirm={handleConfirmBatchMove}
                    onAddToPlan={handleAddBatchToPlan}
                />
            )}

            {/* Bin context menu (right-click) */}
            {editMode && contextMenu && (
                <BinContextMenu
                    x={contextMenu.x}
                    y={contextMenu.y}
                    binCode={contextMenu.binCode}
                    onAction={handleContextMenuAction}
                    onClose={closeContextMenu}
                />
            )}

            {/* Suggest / Empty-bin modal */}
            {editMode && suggestMode && suggestBin && (
                <SuggestAndMoveModal
                    isOpen={true}
                    fromBinCode={suggestBin}
                    mode={suggestMode}
                    isLoading={isLoadingSuggest}
                    suggestions={suggestData}
                    loadError={suggestLoadError}
                    isExecuting={isExecutingSuggest}
                    executeError={suggestExecuteError}
                    executeResult={suggestExecuteResult}
                    onClose={closeSuggestModal}
                    onConfirm={handleConfirmSuggestMove}
                    onAddToPlan={handleAddSuggestToPlan}
                />
            )}

            {/* Add-to-plan modal (chained from any of the three heatmap modals) */}
            {addToPlanSteps && (
                <AddToPlanModal
                    isOpen={true}
                    warehouseID={warehouseID}
                    steps={addToPlanSteps}
                    contextLabel={addToPlanContext}
                    onClose={handleCloseAddToPlan}
                    onAdded={handlePlanAdded}
                />
            )}

            {/* No rack selected message (single-view only) */}
            {viewMode === 'single' && !selectedRackNumber && !isLoading && (
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                        <Activity className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                        <p className="text-lg font-medium">{t('manager.modules.locationHeatmap.empty.selectRackTitle', {}, 'Velg en reol for å vise aktivitetskart')}</p>
                        <p className="text-sm mt-2">{t('manager.modules.locationHeatmap.empty.selectRackHint', {}, 'Klikk på en reol ovenfor for å se aktivitetsnivået')}</p>
                    </div>
                </div>
            )}
        </div>
    );
};
